import React, { useEffect, useState, useCallback, Fragment } from 'react';
import styled from '@emotion/styled';
import Select from 'react-select';
import Skeleton from 'react-loading-skeleton';
import {
    LineChart,
    Line,
    AreaChart,
    Area,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from 'recharts';
import { fetchWrapper, getTeamIdFromURL } from '@/_helpers';
import ComponentErrorBoundary from '@/Layouts/ComponentErrorBoundary';
import moment from 'moment';

const Container = styled.div`
    width: 100%;
    height: 400px;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const Card = styled.div`
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
    padding: 20px;
    flex: 1;
    min-width: 200px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
`;

const Controls = styled.div`
    display: flex;
    gap: 16px;
    align-items: center;
    justify-content: flex-end;
    flex-direction: row;
    margin-bottom: 16px;
`;

const ChartWrapper = styled.div`
    flex: 1;
    width: 100%;
    height: 100%;
`;

const sourceOptions = [
    { value: 'UserFormData', label: 'Submitted Forms' },
    { value: 'Visit', label: 'Visits' },
];

const rangeOptions = [
    { value: '7days', label: 'Last 7 Days' },
    { value: '30days', label: 'Last 30 Days' },
    { value: '90days', label: 'Last 90 Days' },
    { value: '6months', label: 'Last 6 Months' },
    { value: '1year', label: 'Last 1 Year' },
];

async function fetchChartData({ teamId, timeRange, dataSource } = {}) {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return fetchWrapper.get(
        `analytics/team/${teamId}/dashboard-chart?team=${teamId}&currentDate=${new Date().toISOString()}&userTimezone=${userTimezone}&timeRange=${timeRange}&dataSource=${dataSource}`
    );
}

const ChartRenderer = React.memo(({ chart, styles = {} }) => {
    if (!chart || !chart.data || chart.data.length === 0)
        return (
            <div style={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <h5 className="text-muted">No data found for selected time range</h5>
            </div>
        );

    const { chartType, data, dataKeys = [{ key: 'value', stroke: '#4285F4', fill: '#4285F4' }] } = chart;

    const CustomXAxis = <XAxis dataKey="name" height={60} tick={{ fontSize: 12 }} />;
    if (data && Array.isArray(data) && !data.length) {
        return 'no data';
    }
    switch (chartType) {
        case 'line':
            return (
                <ResponsiveContainer style={styles}>
                    <LineChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        {CustomXAxis}
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        {dataKeys.map((dataKey) => (
                            <Line
                                key={dataKey.key}
                                type="monotone"
                                dataKey={dataKey.key}
                                stroke={dataKey.stroke}
                                strokeWidth={2}
                                dot={{ r: 4 }}
                                activeDot={{ r: 6 }}
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            );
        case 'area':
            return (
                <ResponsiveContainer style={styles}>
                    <AreaChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        {CustomXAxis}
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        {dataKeys.map((dataKey) => (
                            <Area
                                key={dataKey.key}
                                type="monotone"
                                dataKey={dataKey.key}
                                stroke={dataKey.stroke}
                                fill={dataKey.fill}
                                fillOpacity={0.2}
                            />
                        ))}
                    </AreaChart>
                </ResponsiveContainer>
            );
        case 'bar':
            return (
                <ResponsiveContainer style={styles}>
                    <BarChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        {CustomXAxis}
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        {dataKeys.map((dataKey) => (
                            <Bar key={dataKey.key} dataKey={dataKey.key} fill={dataKey.fill} />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            );
        default:
            return <div>Unsupported chart type: {chartType}</div>;
    }
});

const Component = () => {
    const [source, setSource] = useState(sourceOptions[0]);
    const [range, setRange] = useState(rangeOptions[0]);
    const [charts, setCharts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
    const teamId = getTeamIdFromURL();
    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            setCharts([]);
            const payload = {
                dataSource: source.value,
                teamId: teamId,
                timeRange: range.value,
                currentDate: new Date().toISOString(),
            };
            const response = await fetchChartData(payload);
            setCharts(response?.results || []);
        } catch (err) {
            console.error('Failed to load chart data', err);
        } finally {
            setLoading(false);
        }
    }, [source, range]);

    useEffect(() => {
        loadData();
    }, [source, range]);

    useEffect(() => {
        if (charts && charts.length) {
            setLastUpdatedAt(charts[0]?.lastUpdatedAt || null);
        }
    }, [charts]);

    return (
        <Container>
            <Card>
                <div className="d-flex justify-content-between flex-row ">
                    <div className="ml-4 d-flex flex-column">
                        <div>
                            <strong>Team Analytics</strong>
                        </div>
                        <span className="text-muted " style={{ fontSize: '12px' }}>
                            {lastUpdatedAt ? (
                                <span> Last updated {moment(lastUpdatedAt).fromNow()}</span>
                            ) : (
                                <span>Will be updated every 60 minutes</span>
                            )}
                        </span>
                    </div>
                    <Controls>
                        <Select
                            isDisabled={loading}
                            options={sourceOptions}
                            value={source}
                            onChange={setSource}
                            styles={{ container: (base) => ({ ...base, width: 200 }) }}
                        />
                        <Select
                            isDisabled={loading}
                            options={rangeOptions}
                            value={range}
                            onChange={setRange}
                            styles={{ container: (base) => ({ ...base, width: 200 }) }}
                        />
                    </Controls>
                </div>

                <ChartWrapper>
                    {loading ? (
                        <Skeleton height="100%" width="100%" baseColor="#f0f2f5" highlightColor="#e0e2e7" />
                    ) : (
                        <Fragment>
                            {(charts || []).map((chart, index) => (
                                <ChartRenderer
                                    key={chart?.id || index}
                                    chart={chart}
                                    styles={{ marginLeft: '-30px' }}
                                />
                            ))}
                        </Fragment>
                    )}
                </ChartWrapper>
            </Card>
        </Container>
    );
};

const DashboardChart = (props) => (
    <ComponentErrorBoundary componentErrorMessage={'Failed to load chart'}>
        <Component {...props} />
    </ComponentErrorBoundary>
);

export default DashboardChart;
